import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { LogOut, X } from "Shared/appIcons";
import {
	NavButton,
	NavGroup,
	type NavItem,
} from "Shared/Layouts/components/NavButton";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

type Props = {
	open: boolean;
	onClose: () => void;
	navItems: NavItem[];
	isActive: (to?: string) => boolean;
	userLogin: string;
	userInitials: string;
	onSignOut: () => void;
};

/**
 * Off-canvas drawer navigation: hidden on `md` and up where `TopbarNav`
 * takes over. The trigger (hamburger) lives in `DrawerTopbar`.
 */
export const DrawerNav = ({
	open,
	onClose,
	navItems,
	isActive,
	userLogin,
	userInitials,
	onSignOut,
}: Props) => {
	const { t } = useResource();
	const top = navItems.filter((i) => i.group === "top");
	const bottom = navItems.filter((i) => i.group === "bottom");
	const hasBottom = bottom.length > 0;

	return (
		<>
			{open ? (
				<div
					aria-hidden="true"
					onClick={onClose}
					className="bg-foreground/40 fixed inset-0 z-30 backdrop-blur-sm md:hidden"
				/>
			) : null}

			<aside
				className={cn(
					"border-sidebar-border bg-sidebar text-sidebar-foreground fixed inset-y-0 left-0 z-40 flex w-60 flex-col border-r transition-transform duration-200 ease-out md:hidden",
					open ? "translate-x-0" : "-translate-x-full",
				)}
			>
				<div className="border-border flex h-14 items-center justify-between border-b pr-2 pl-5">
					<div className="flex min-w-0 flex-1 items-center">
						<div className="bg-primary flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md">
							<span className="text-primary-foreground text-[13px] font-bold tracking-tight">
								IL
							</span>
						</div>
						<div className="ml-2.5 flex min-w-0 flex-col overflow-hidden leading-tight">
							<span className="text-foreground truncate text-[13px] font-semibold">
								{t(Resources.Sidebar.BrandTitle)}
							</span>
							<span className="text-muted-foreground truncate text-[10px]">
								{t(Resources.Sidebar.BrandSubtitle)}
							</span>
						</div>
					</div>
					<Button
						variant="ghost"
						size="icon-sm"
						onClick={onClose}
						className="text-muted-foreground hover:text-foreground flex-shrink-0"
						title={t(Resources.Common.Close)}
					>
						<X className="h-4 w-4" />
					</Button>
				</div>

				<nav className="text-small flex-1 overflow-x-hidden overflow-y-auto px-2 py-3">
					<NavGroup>
						{top.map((item) => (
							<NavButton
								key={item.key}
								item={item}
								active={isActive(item.to)}
								collapsed={false}
							/>
						))}
					</NavGroup>

					{hasBottom ? (
						<div className="mt-6">
							<NavGroup>
								{bottom.map((item) => (
									<NavButton
										key={item.key}
										item={item}
										active={isActive(item.to)}
										collapsed={false}
									/>
								))}
							</NavGroup>
						</div>
					) : null}
				</nav>

				<div className="border-border flex items-center gap-2.5 border-t px-4 py-3">
					<div
						className="bg-primary-light text-primary text-small flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full font-semibold"
						title={userLogin}
					>
						{userInitials}
					</div>
					<div className="min-w-0 flex-1 overflow-hidden">
						<div className="text-small text-foreground truncate font-medium">
							{userLogin}
						</div>
					</div>
					<Button
						variant="ghost"
						size="icon-sm"
						onClick={onSignOut}
						className="text-muted-foreground hover:text-foreground flex-shrink-0"
						title={t(Resources.Common.SignOut)}
					>
						<LogOut className="h-4 w-4" />
					</Button>
				</div>
			</aside>
		</>
	);
};
